import {inject, Injectable, signal} from '@angular/core';
import {firstValueFrom, Subject, takeUntil, timeout, TimeoutError} from 'rxjs';
import {Router} from '@angular/router';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse} from '@angular/common/http';
import {ENV_TOKEN, EnvInterface} from 'env-config';
import {AuthValidationService} from './services/auth-validation.service';
import {decodeJwtPayload} from './utils/jwt';

/**
 * Progress of an inbound SSO handoff from Kentico.
 * - exchanging: first call to /auth/sso-inbound in flight (most handoffs finish here)
 * - provisioning: RevisionHub is still receiving the account/licence from Kentico; retrying
 * - slow: provisioning has not finished within the deadline; the user can try again
 * - failed: the handoff cannot succeed (invalid, reused or unknown token)
 */
export type SsoHandoffState = 'idle' | 'exchanging' | 'provisioning' | 'slow' | 'failed';

/** Guard-free route that shows handoff progress, so no guard can act before the session exists. */
export const SSO_WAITING_PATH = '/getting-ready';

interface PendingSsoHandoff {
  token: string;
  returnUrl: string;
  /**
   * True while an attempt with this token might have been consumed by the server without this tab
   * learning the result: set **before** each request is sent, cleared by a 202 (proof the token is
   * still unused), and left set by a timeout, network error or 5xx. Stored with the token, not in the
   * run, so "Try again" and a reload of the waiting page, even one made while a request was still
   * outstanding, recover rather than fail (reviews of TFS.69299).
   */
  outcomeUncertain?: boolean;
}

/**
 * Body of an /auth/sso-inbound response, as the API sends it (SsoInboundStatusResponse in the API).
 *
 * `state` and `reason` are the API's response codes, upper case like its other codes (e.g. reason
 * NO_ACCOUNT). They are deliberately separate from SsoHandoffState, the wrapper's own page states,
 * which include steps the server never reports ('idle', 'exchanging', 'slow'). The wrapper decides
 * what to do from the HTTP status (200, 202, 4xx, 5xx). The one exception is reason TOKEN_REVOKED,
 * which only changes how a failure is recovered (see recoverUsedHandoff), never whether it is one.
 */
interface SsoInboundResponse {
  token?: string;
  accessToken?: string;
  refreshToken?: string;
  state?: 'PROVISIONING' | 'FAILED';
  reason?: string;
  retryAfterMs?: number;
}

/** What one attempt at /auth/sso-inbound came to; see AuthService.sendAttempt. */
type AttemptResult =
  | {kind: 'signedIn', accessToken: string}
  | {kind: 'provisioning', retryAfterMs: number}
  | {kind: 'retry'}       // timeout, network error or 5xx: try the same token again
  | {kind: 'tokenUsed'}   // 401 TOKEN_REVOKED
  | {kind: 'rejected'};   // any other 4xx, or a success with no token

/** One run of the exchange loop; see AuthService.startRun. */
interface ExchangeRun {
  readonly deadline: number;
  isCurrent(): boolean;
  loggedOutSinceStart(): boolean;
}

const PENDING_SSO_KEY = 'pending-sso-handoff';
/** Set once the user has picked a product this session; read by the app shell and the root guard. */
const PRODUCT_SELECTED_KEY = 'productSelected';
const PROVISIONING_DEADLINE_MS = 60_000;
const MIN_RETRY_DELAY_MS = 500;
const MAX_RETRY_DELAY_MS = 3_000;
const DEFAULT_RETRY_DELAY_MS = 1_000;
/** When the waiting page last sent the browser back to Kentico on its own; see recoverUsedHandoff. */
const KENTICO_RETRY_AT_KEY = 'sso-kentico-retry-at';
/** At most one automatic trip back to Kentico in this window, so a repeating failure cannot loop. */
const KENTICO_AUTO_RETRY_WINDOW_MS = 5 * 60_000;

@Injectable({providedIn: 'root'})
export class AuthService {
  private environment = inject(ENV_TOKEN) as EnvInterface;
  apiKey = this.environment.security.tuatara.apiKey;
  private authValidationService = inject(AuthValidationService);

  readonly ssoState = signal<SsoHandoffState>('idle');

  /**
   * Where "Try again" goes once the handoff token itself can no longer work (used, or expired): back
   * to the Kentico site that issued it, whose SsoRedirectHandler mints a fresh token from the user's
   * Kentico session. Null when the token names no allowed Kentico site, or is a pupil token; the page
   * then offers only "Log in". Kept after a failure deletes the pending token, for the failure page.
   */
  readonly ssoKenticoRetryUrl = signal<string | null>(null);

  /*
   * Inbound SSO handoff rules (TFS.69299, INV-013). Each was added in response to review, so keep
   * them all when changing this code. Each is explained where it is enforced:
   *
   * - Only one exchange at a time, because the token is single-use: exchangeSsoToken.
   * - A cancelled run never stores a session; cancel on explicit logout in any tab and on leaving
   *   the waiting page: startRun, cancelSsoHandoff, and the explicitLogout$ subscription below.
   * - The 60s deadline is hard, and the first attempt always goes out: runExchange, sendAttempt.
   * - Network errors, timeouts and 5xx retry the same token; 401, 403 and 404 are final: sendAttempt.
   * - "Possibly used" is stored before each request, so Try again and reloads still recover:
   *   runExchange, PendingSsoHandoff.outcomeUncertain.
   * - A used token after a lost reply is recovered through Kentico, never by a silent refresh, and
   *   not after a logout since the run began: recoverUsedHandoff.
   * - A logout since the run began also stops a session being stored: completeSignIn.
   */

  /** Incremented to supersede the running handoff; see runExchange. */
  private handoffGeneration = 0;
  /** Emits on cancel, to abort the request in flight. */
  private readonly handoffCancelled$ = new Subject<void>();
  /** The exchange in progress, so a second start joins it (single-flight). */
  private activeExchange: Promise<void> | null = null;

  constructor(private router: Router, private http: HttpClient) {
    // An explicit logout, in this tab or another, must win over a handoff still waiting to sign
    // the user in
    this.authValidationService.explicitLogout$.subscribe(() => this.cancelSsoHandoff());
  }

  getSharedCookie(): string | null {
    const match = document.cookie
      .match(/(?:^|; )SharedAuthCookie=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  checkSharedLogin(): void {
    //console.log('Checking for shared login cookie...');
    const token = this.getSharedCookie();
    if (token) {
      //console.log('Shared login cookie found:', token);
      // TODO: Validate token with your API and log the user in
      // this.validateAndLogin(token);
    } else {
      //console.log('No shared login cookie found.');
    }
  }

  async bootstrapAuth(): Promise<void> {
    // Check for inbound SSO token from Kentico
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('sso');

    if (ssoToken) {
      params.delete('sso');
      const returnUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      await this.beginSsoHandoff({token: ssoToken, returnUrl});
      return;
    }

    // A refresh on the waiting page resumes the handoff that was in progress
    const pending = this.readPendingSsoHandoff();
    if (pending && window.location.pathname === SSO_WAITING_PATH) {
      await this.beginSsoHandoff(pending);
      return;
    }

    // Normal JWT check — consider logged in if access token is present
    // (new tab case is handled by authGuard calling tryRefresh() via the HttpOnly cookie)
    const stored = localStorage.getItem('auth-token');
    const loggedIn = this.authValidationService.getStoredToken();
    if (stored || loggedIn)
      return;
    //if (stored && !this.isExpired(stored)) return;

    // Not logged in
    //TODO Dont do this if you are coming from a redirect (jumplink)
    //this.router.navigate(['/login']);
  }

  /** True while the pending handoff token is still within its lifetime, so trying again can succeed. */
  canRetrySsoHandoff(): boolean {
    const pending = this.readPendingSsoHandoff();
    const exp = decodeJwtPayload(pending?.token)?.exp;
    return typeof exp === 'number' && exp * 1000 > Date.now();
  }

  async retrySsoHandoff(): Promise<void> {
    const pending = this.readPendingSsoHandoff();
    if (pending) {
      await this.exchangeSsoToken(pending);
    }
  }

  /**
   * Stops any handoff in progress and forgets its token: on explicit logout, or when the user leaves
   * the waiting page. A request still in flight is cancelled, and a late response is ignored.
   */
  cancelSsoHandoff(): void {
    this.handoffGeneration++;
    this.activeExchange = null;
    this.handoffCancelled$.next();
    sessionStorage.removeItem(PENDING_SSO_KEY);
    this.ssoKenticoRetryUrl.set(null);
    this.ssoState.set('idle');
  }

  private async beginSsoHandoff(pending: PendingSsoHandoff): Promise<void> {
    // Synchronous until the first await: the token leaves the URL and the router's initial
    // navigation lands on the waiting page, before any guard can redirect a not-yet-signed-in user.
    sessionStorage.setItem(PENDING_SSO_KEY, JSON.stringify(pending));
    window.history.replaceState({}, '', SSO_WAITING_PATH);
    this.ssoKenticoRetryUrl.set(this.kenticoRetryUrl(pending));
    await this.exchangeSsoToken(pending);
  }

  /**
   * The issuing Kentico site's SsoRedirectHandler, returning to the page the user was going to. Built
   * from the token's iss claim, but only for origins in security.kenticoOrigins. The wrapper cannot
   * check the token's signature, so without that allowlist a crafted ?sso= link could send the
   * browser anywhere. Pupil tokens get none: a pupil isn't signed in to Kentico, so it couldn't help.
   */
  private kenticoRetryUrl(pending: PendingSsoHandoff): string | null {
    const claims = decodeJwtPayload(pending.token);
    if (typeof claims?.iss !== 'string' || claims.isPupilMember === true) {
      return null;
    }
    try {
      const issuer = new URL(claims.iss).origin;
      const allowed = (this.environment.security.kenticoOrigins ?? []).map(origin => new URL(origin).origin);
      if (!allowed.includes(issuer)) {
        return null;
      }
      const returnTo = new URL(pending.returnUrl || '/', window.location.origin).toString();
      return `${issuer}/SsoRedirectHandler.ashx?returnUrl=${encodeURIComponent(returnTo)}`;
    } catch {
      return null;
    }
  }

  /**
   * Single-flight: starting an exchange while one is running joins it. Two runs would race for the
   * single-use token, and the loser's 401 could fail a handoff the winner had already completed.
   */
  private exchangeSsoToken(pending: PendingSsoHandoff): Promise<void> {
    if (!this.activeExchange) {
      const run: Promise<void> = this.runExchange(pending).finally(() => {
        if (this.activeExchange === run) {
          this.activeExchange = null;
        }
      });
      this.activeExchange = run;
    }
    return this.activeExchange;
  }

  /**
   * Exchanges the Kentico token for a RevisionHub session. A brand-new customer's account and licences
   * reach RevisionHub asynchronously, so the API may answer 202 PROVISIONING; retry the same token
   * with a growing delay until it is ready or the deadline passes.
   *
   * Each pass of the loop is one attempt, and reads in the same order as the workflow diagram
   * ("RevisionHub SSO Handoff - Exchange Loop" in the vault): time left? → mark possibly used →
   * send → act on the result → wait → again. The rules each step must keep are on its helper.
   */
  private async runExchange(pending: PendingSsoHandoff): Promise<void> {
    const run = this.startRun();

    // No check on the current state is needed here. runExchange is only entered through the
    // single-flight exchangeSsoToken, so no other exchange is running, and the state can only be
    // 'idle' (a new handoff, or after a cancel) or 'slow' ("Try again"). It can't be 'failed': a
    // failure deletes the pending token, so a retry finds nothing to run. Cancelled runs never write
    // the state again, because every later write follows a run.isCurrent() check.
    //
    // A new handoff is 'exchanging' until the API first answers 202; a retry from 'slow' is already
    // known to be provisioning. The waiting page shows the same welcome message for both.
    this.ssoState.set(this.ssoState() === 'slow' ? 'provisioning' : 'exchanging');

    for (let attempt = 1; ; attempt++) {
      const remainingMs = run.deadline - Date.now();
      // The first attempt always goes out, so a fast client clock can't discard a valid token.
      // Retries stop once the token has expired, since the API would only reject it.
      if (remainingMs <= 0 || (attempt > 1 && !this.canRetrySsoHandoff())) {
        this.ssoState.set('slow');
        return;
      }

      // Whether an earlier attempt, in this run or before a reload or Try again, may have used the
      // token. Read before marking this attempt, which is marked before it is sent: a reload while
      // the request is out never gets as far as its result, and the server may use the token anyway.
      const mayAlreadyBeUsed = pending.outcomeUncertain === true;
      this.setOutcomeUncertain(pending, true);

      const result = await this.sendAttempt(pending, remainingMs);
      if (!run.isCurrent()) {
        return;
      }

      switch (result.kind) {
        case 'signedIn':
          this.completeSignIn(pending, result.accessToken, run);
          return;
        case 'tokenUsed':
          // Only recoverable if an earlier attempt may have used it; otherwise it is a reused token
          if (mayAlreadyBeUsed) {
            this.recoverUsedHandoff(pending, run);
          } else {
            this.onSsoHandoffFailed();
          }
          return;
        case 'rejected':
          this.onSsoHandoffFailed();
          return;
        case 'provisioning':
          // A 202 proves the token is still unused, so nothing so far used it
          this.setOutcomeUncertain(pending, false);
          this.ssoState.set('provisioning');
          break;
        case 'retry':
          // Network blip, gateway error or timeout: the token stays marked as possibly used
          break;
      }

      const retryAfterMs = result.kind === 'provisioning' ? result.retryAfterMs : DEFAULT_RETRY_DELAY_MS;
      await this.delayWithinDeadline(this.retryDelay(retryAfterMs, attempt), run.deadline);
      if (!run.isCurrent()) {
        return;
      }
    }
  }

  /**
   * Starts a new run, superseding any other: cancelling bumps handoffGeneration, so isCurrent() turns
   * false for every older run, and each one checks it after every await. A cancelled run therefore
   * never stores a session or changes the page.
   *
   * loggedOutSinceStart() compares the logout id now with the one when the run began: an explicit
   * logout in any tab writes a new id, even one whose storage event this tab missed (e.g. while it was
   * suspended). An id rather than the boolean flag, which may already be true when the run begins and
   * so cannot show a new logout.
   */
  private startRun(): ExchangeRun {
    const generation = ++this.handoffGeneration;
    const logoutIdAtStart = this.authValidationService.getLogoutId();
    return {
      deadline: Date.now() + PROVISIONING_DEADLINE_MS,
      isCurrent: () => generation === this.handoffGeneration,
      loggedOutSinceStart: () => this.authValidationService.getLogoutId() !== logoutIdAtStart,
    };
  }

  /**
   * Sends one attempt and says what happened, as a value rather than a mix of statuses and exceptions.
   *
   * The 60s deadline is hard: the request is cut off at the time remaining. A cancel aborts it through
   * handoffCancelled$; the caller then sees the run is no longer current and ignores the result.
   *
   * Timeouts, network errors and 5xx are 'retry': the API leaves the token unused on every non-200
   * response it actually sends, but an attempt whose reply never arrives may still have used it, which
   * is why the token stays marked. 401, 403 and 404 never succeed on retry. A 401 TOKEN_REVOKED is told
   * apart only so the caller can decide whether it is recoverable; it never turns a rejection into a
   * retry. The API's state and reason codes are otherwise not used: see SsoInboundResponse.
   */
  private async sendAttempt(pending: PendingSsoHandoff, remainingMs: number): Promise<AttemptResult> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': this.apiKey,
    });
    try {
      const response = await firstValueFrom(
        this.http.post<SsoInboundResponse>(
          `${this.environment.security.tuatara.endpoint}/auth/sso-inbound`,
          {token: pending.token, acceptPending: true},
          {headers, observe: 'response'}
        ).pipe(
          // A hung request must not outlive the deadline
          timeout(remainingMs),
          takeUntil(this.handoffCancelled$),
        )
      );
      if (response.status === 202) {
        return {kind: 'provisioning', retryAfterMs: response.body?.retryAfterMs ?? DEFAULT_RETRY_DELAY_MS};
      }
      // Support both { token } and { accessToken } response shapes
      const accessToken = response.body?.accessToken ?? response.body?.token;
      return accessToken ? {kind: 'signedIn', accessToken} : {kind: 'rejected'};
    } catch (err) {
      if (this.isRecoverable(err)) {
        return {kind: 'retry'};
      }
      return this.isTokenRevoked(err) ? {kind: 'tokenUsed'} : {kind: 'rejected'};
    }
  }

  /**
   * Network failures, timeouts and server-side errors may clear on retry, and the API leaves the
   * token unused on them, unless the attempt in fact succeeded and only its response was lost (see
   * recoverUsedHandoff). Rejections (401, 403, 404, other 4xx) never will.
   */
  private isRecoverable(err: unknown): boolean {
    if (err instanceof TimeoutError) {
      return true;
    }
    if (err instanceof HttpErrorResponse) {
      return err.status === 0 || err.status >= 500;
    }
    return false;
  }

  private isTokenRevoked(err: unknown): boolean {
    return err instanceof HttpErrorResponse && err.status === 401 && err.error?.reason === 'TOKEN_REVOKED';
  }

  /**
   * Records on the stored pending handoff whether the server may have used its token; see
   * PendingSsoHandoff. Only called while the run is current, so it never recreates a pending handoff
   * that a cancel has removed.
   */
  private setOutcomeUncertain(pending: PendingSsoHandoff, uncertain: boolean): void {
    pending.outcomeUncertain = uncertain;
    sessionStorage.setItem(PENDING_SSO_KEY, JSON.stringify(pending));
  }

  /**
   * Stores the session and reloads the page the user was going to, so guards run with it. The
   * HttpOnly refresh cookie was set by the server in the response; Angular doesn't handle it.
   *
   * A logout since the run began wins: the handoff is cancelled instead.
   */
  private completeSignIn(pending: PendingSsoHandoff, accessToken: string, run: ExchangeRun): void {
    if (run.loggedOutSinceStart()) {
      this.cancelSsoHandoff();
      return;
    }
    this.authValidationService.storeToken(accessToken);
    sessionStorage.removeItem(PENDING_SSO_KEY);
    if (this.isSpecificDestination(pending.returnUrl)) {
      // The user chose a product by following a Kentico link to it (e.g. the Megamenu's
      // "RevisionHub" link to /revision-hub), so don't ask them to choose again. Without this, a
      // signed-in user who owns nothing yet is sent on to /product-selector by the app shell's
      // no-products rule (app.ts). Same flag as ProductSelectorComponent sets; cleared on logout.
      sessionStorage.setItem(PRODUCT_SELECTED_KEY, 'true');
    }
    this.navigateTo(pending.returnUrl || '/');
  }

  /**
   * True when the handoff was for a particular page rather than the site root or the product
   * selector itself, i.e. the user picked where to go. 11+ Online links are unaffected either way:
   * 11+ owners are never caught by the no-products rule, and Kentico sends non-owners to its shop
   * instead of handing them off.
   */
  private isSpecificDestination(returnUrl: string): boolean {
    try {
      const path = new URL(returnUrl || '/', window.location.origin).pathname.replace(/\/+$/, '');
      return path !== '' && path !== '/product-selector';
    } catch {
      return false;
    }
  }

  /**
   * The token was used, but by an attempt whose response never arrived, so the server may have
   * signed the user in without this tab knowing (review of TFS.69299). Recovers instead of failing:
   *
   * 1. Back to the issuing Kentico site for a fresh token (ssoKenticoRetryUrl). Done automatically
   *    at most once per KENTICO_AUTO_RETRY_WINDOW_MS, so a failure that repeats lands on the failure
   *    page, whose "Try again" goes the same way, rather than looping.
   * 2. Otherwise, the usual failure.
   *
   * A logout since the run began wins, as it does before storing a session: the handoff is cancelled
   * instead, because a redirect to Kentico would sign a still-signed-in Kentico user straight back in.
   *
   * Deliberately no silent refresh, even though the lost response may have set a refresh cookie. A
   * refresh stores the token for whoever the cookie belongs to, and clears the explicit-logout flag,
   * before any caller can check either. So it could sign the user back in after they logged out
   * meanwhile, or leave the browser signed in as someone else whose older cookie was still there.
   * Kentico issues a token for the right user, from their Kentico session, at the cost of a redirect.
   *
   * Synchronous, so no cancellation or logout can arrive part-way through; the caller has just
   * checked the run is current.
   *
   * The token is never retried here: it is used, and making used tokens reusable would undo single
   * use.
   */
  private recoverUsedHandoff(pending: PendingSsoHandoff, run: ExchangeRun): void {
    if (run.loggedOutSinceStart()) {
      this.cancelSsoHandoff();
      return;
    }
    // From the token, not only the signal, so a run started by "Try again" alone recovers too
    const retryUrl = this.ssoKenticoRetryUrl() ?? this.kenticoRetryUrl(pending);
    this.ssoKenticoRetryUrl.set(retryUrl);
    const lastAutoRetry = Number(sessionStorage.getItem(KENTICO_RETRY_AT_KEY) ?? 0);
    if (retryUrl && Date.now() - lastAutoRetry > KENTICO_AUTO_RETRY_WINDOW_MS) {
      sessionStorage.setItem(KENTICO_RETRY_AT_KEY, String(Date.now()));
      sessionStorage.removeItem(PENDING_SSO_KEY);
      this.navigateTo(retryUrl);
      return;
    }

    this.onSsoHandoffFailed();
  }

  private retryDelay(retryAfterMs: number, attempt: number): number {
    // Floor guards against a zero or tiny server hint turning this into a tight loop
    return Math.min(Math.max(retryAfterMs, MIN_RETRY_DELAY_MS) * attempt, MAX_RETRY_DELAY_MS);
  }

  /** Waits, but never past the deadline; the loop then reports 'slow' instead of starting a request. */
  private async delayWithinDeadline(ms: number, deadline: number): Promise<void> {
    await this.delay(Math.max(0, Math.min(ms, deadline - Date.now())));
  }

  private onSsoHandoffFailed(): void {
    sessionStorage.removeItem(PENDING_SSO_KEY);
    if (this.authValidationService.getStoredToken()) {
      // Already signed in on this tab: carry on with the existing session
      this.ssoState.set('idle');
      this.router.navigate(['/']);
    } else {
      this.ssoState.set('failed');
    }
  }

  private readPendingSsoHandoff(): PendingSsoHandoff | null {
    try {
      return JSON.parse(sessionStorage.getItem(PENDING_SSO_KEY) ?? 'null');
    } catch {
      return null;
    }
  }

  protected navigateTo(url: string): void {
    window.location.href = url;
  }

  protected delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private decodeAccount(token: string): any {
    return decodeJwtPayload(token);
  }
}
